import { EyeOutlined } from '@ant-design/icons'
import { Button, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { CampaignEmployee } from '../../models/Campaign'
import { SignModal } from './SignModal'
import { SignSummary } from './SignSummary'

const { Text } = Typography

interface Props {
  campaignEmployee: CampaignEmployee
  role: 'employee' | 'manager'
  readonly?: boolean
}

export const CampaignEmployeeStatus = ({
  campaignEmployee,
  role,
  readonly = false,
}: Props) => {
  const renderEmployeeControls = () => {
    if (campaignEmployee.managerSignedAt && campaignEmployee.employeeSignedAt) {
      return (
        <Space>
          <Text>Entretien clos</Text>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button>Voir</Button>
          </Link>
          <SignSummary campaignEmployee={campaignEmployee} isEmployee={true} />
        </Space>
      )
    }

    if (
      campaignEmployee.employeeSignedAt &&
      !campaignEmployee.managerSignedAt
    ) {
      return (
        <Space vertical style={{ alignItems: 'center' }}>
          <Text>En attente de signature par votre manager</Text>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button type={'primary'} icon={<EyeOutlined />}>
              Voir
            </Button>
          </Link>
        </Space>
      )
    }

    if (
      campaignEmployee.employeeFulfilledAt &&
      campaignEmployee.managerFulfilledAt
    ) {
      return (
        <Space>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button>Voir</Button>
          </Link>
          {!readonly && (
            <SignModal campaignEmployee={campaignEmployee} isEmployee={true} />
          )}
        </Space>
      )
    }

    if (
      campaignEmployee.employeeFulfilledAt &&
      !campaignEmployee.managerFulfilledAt
    ) {
      return (
        <Space vertical style={{ alignItems: 'center' }}>
          <Text>En attente de validation par votre manager</Text>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button type={'primary'} icon={<EyeOutlined />}>
              Voir
            </Button>
          </Link>
        </Space>
      )
    }

    return (
      <Space vertical style={{ alignItems: 'center' }}>
        <Text>
          À soumettre avant le{' '}
          {dayjs(campaignEmployee.campaign.softEnd).format('DD/MM/YYYY')}
        </Text>
        {!readonly && (
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button size="large" type="primary">
              Remplir le formulaire
            </Button>
          </Link>
        )}
      </Space>
    )
  }

  const renderManagerControls = () => {
    if (campaignEmployee.managerSignedAt && campaignEmployee.employeeSignedAt) {
      return (
        <Space>
          <Text>Entretien clos</Text>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button type="default" icon={<EyeOutlined />} />
          </Link>
          <SignSummary campaignEmployee={campaignEmployee} isEmployee={false} />
        </Space>
      )
    }

    if (
      campaignEmployee.employeeFulfilledAt &&
      !campaignEmployee.managerFulfilledAt
    ) {
      return (
        <Space>
          {!readonly && (
            <Link to={`/campaigns/${campaignEmployee.id}`}>
              <Button type="primary">Répondre</Button>
            </Link>
          )}
        </Space>
      )
    }

    if (
      campaignEmployee.employeeFulfilledAt &&
      campaignEmployee.managerFulfilledAt &&
      !campaignEmployee.employeeSignedAt
    ) {
      return (
        <Space>
          <Text>En attente de la signature de l&apos;employée</Text>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button type="default" icon={<EyeOutlined />} />
          </Link>
        </Space>
      )
    }
    if (
      campaignEmployee.employeeFulfilledAt &&
      campaignEmployee.managerFulfilledAt &&
      campaignEmployee.employeeSignedAt
    ) {
      return (
        <Space>
          <Link to={`/campaigns/${campaignEmployee.id}`}>
            <Button type="default" icon={<EyeOutlined />} />
          </Link>
          {!readonly && (
            <SignModal campaignEmployee={campaignEmployee} isEmployee={false} />
          )}
        </Space>
      )
    }

    return null
  }

  return (
    <>
      {role === 'employee' ? renderEmployeeControls() : renderManagerControls()}
    </>
  )
}

export type CampaignEmployeeStatusType = {
  Status: typeof CampaignEmployeeStatus
}
